import { Dispatch, FormEvent, SetStateAction, useEffect, useState } from "react";

import { acceptInvitation } from "../../../../service/identity";
import { ServiceError, Status } from "../../../../service/Shared";
import { useIsLoadingWrapper } from "../../../../utils";

interface UseAcceptInvitationFormReturnData {
  emailOverride: string;
  newPassword: string;
  newPasswordConfirmation: string;
  errors: ServiceError[] | undefined;
  firstNameOverride: string;
  isHandleSubmitLoading: boolean;
  lastNameOverride: string;
  setEmailOverride: Dispatch<SetStateAction<string>>;
  setFirstNameOverride: Dispatch<SetStateAction<string>>;
  setNewPassword: Dispatch<SetStateAction<string>>;
  setNewPasswordConfirmation: Dispatch<SetStateAction<string>>;
  setLastNameOverride: Dispatch<SetStateAction<string>>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

export const useAcceptInvitationForm = (
  urlToken: string,
  email: string,
  firstName: string,
  lastName: string,
  requireOverrideFields: boolean,
  onSuccess: (e: boolean) => void
): UseAcceptInvitationFormReturnData => {
  const [emailOverride, setEmailOverride] = useState(email);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("");
  const [errors, setErrors] = useState<ServiceError[] | undefined>([]);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [firstNameOverride, setFirstNameOverride] = useState(firstName);
  const [lastNameOverride, setLastNameOverride] = useState(lastName);

  useEffect(() => {
    setEmailOverride(email);
    setFirstNameOverride(firstName);
    setLastNameOverride(lastName);
  }, [urlToken, email, firstName, lastName]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const validationErrors: ServiceError[] = [];

    if (!email) {
      validationErrors.push({
        code: "INVITATION_EMAIL_NOT_FOUND",
        message: "We could not load the invited email address from this link.",
      });
    }

    if (newPassword !== newPasswordConfirmation) {
      validationErrors.push({
        code: "NOT_MATCHING_PASSWORDS",
        message: "The passwords do not match",
      });
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const res = await acceptInvitation({
      email,
      token: urlToken,
      password: newPassword,
      emailOverride: requireOverrideFields ? emailOverride : null,
      firstNameOverride: requireOverrideFields ? firstNameOverride : null,
      lastNameOverride: requireOverrideFields ? lastNameOverride : null,
      shouldValidateOverrideFields: requireOverrideFields,
    });

    if (res.status === Status.Success) {
      onSuccess(true);
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  return {
    emailOverride,
    newPassword,
    newPasswordConfirmation,
    errors,
    firstNameOverride,
    isHandleSubmitLoading,
    lastNameOverride,
    setEmailOverride,
    setFirstNameOverride,
    setNewPassword,
    setNewPasswordConfirmation,
    setLastNameOverride,
    handleSubmit,
  };
};
